
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, Grid, List, MapPin } from 'lucide-react';
import { BedListing } from '@/types/listings';
import ListingCard from './ListingCard';

interface SearchResultsProps {
  listings: BedListing[];
  loading: boolean;
  sortBy: string;
  viewMode: 'grid' | 'list';
  onSortChange: (sort: string) => void;
  onViewModeChange: (mode: 'grid' | 'list') => void;
  onListingClick: (listing: BedListing) => void;
  searchTerm: string;
  totalResults: number;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  listings,
  loading,
  sortBy,
  viewMode,
  onSortChange,
  onViewModeChange,
  onListingClick,
  searchTerm,
  totalResults,
}) => {
  const getSortLabel = (sort: string) => {
    switch (sort) {
      case 'last_updated': return 'Most Recent';
      case 'rent_asc': return 'Price: Low to High';
      case 'rent_desc': return 'Price: High to Low';
      case 'beds_desc': return 'Most Beds Available';
      case 'facility_name': return 'Facility Name (A-Z)';
      default: return 'Most Recent';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading listings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Results Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">
                {totalResults === 0 ? 'No Results' : `${totalResults} Result${totalResults !== 1 ? 's' : ''}`}
              </CardTitle>
              {searchTerm && (
                <Badge variant="outline">
                  for "{searchTerm}"
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Sort Controls */}
              <Select value={sortBy} onValueChange={onSortChange}>
                <SelectTrigger className="w-[180px]">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_updated">Most Recent</SelectItem>
                  <SelectItem value="rent_asc">Price: Low to High</SelectItem>
                  <SelectItem value="rent_desc">Price: High to Low</SelectItem>
                  <SelectItem value="beds_desc">Most Beds Available</SelectItem>
                  <SelectItem value="facility_name">Facility Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode Toggle */}
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onViewModeChange('grid')}
                  className="rounded-r-none"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onViewModeChange('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Results */}
      {listings.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No listings found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search criteria or filters to find more results.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 lg:grid-cols-2 gap-6'
            : 'space-y-4'
        }>
          {listings.map(listing => (
            <ListingCard
              key={listing.id}
              listing={listing}
              onViewDetails={onListingClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
